// ═══════════════════════════════════════════════════════════════
// CALCULATEUR PUBLIC BRUT → NET, PAR COMMISSION PARITAIRE
//
// POURQUOI CES PAGES EXISTENT (23/08/2026)
// Audit SEO du 23/08 (docs/30-39 Commercial/30.27) : sur les QUATRE requêtes
// « calculateur / simulateur de salaire » mesurées, FR et NL, le site est
// ABSENT des quatre. Ceux qui les gagnent — loonsimulator.be, loonberekening.be,
// salaireclair.fr — sont des pure-players SANS produit. Nous avons un moteur
// validé 29/29 contre des fiches Liantis réelles, et AUCUNE page publique ne
// l'exposait par commission paritaire.
//
// CE QUI DIFFÈRE RÉELLEMENT D'UNE CP À L'AUTRE — vérifié, pas supposé :
//   1. le MINIMUM d'entrée vérifié (donc le brut de départ de la page) ;
//   2. le STATUT ouvrier/employé : la base ONSS d'un ouvrier est portée à
//      108 % (LB.onss.ouvrier108), ce qui change le net ET le coût employeur ;
//   3. le FONDS SECTORIEL patronal — mesuré : 119 des 121 CP publiables en ont
//      un taux connu, donc le coût employeur diffère vraiment page par page.
// Ce ne sont donc pas 121 pages jumelles : chacune porte ses propres chiffres.
//
// ⚠️ CE QUI NE DÉPEND PAS DE LA CP, ET QU'IL NE FAUT PAS LAISSER CROIRE :
// `calcPayroll` n'accepte AUCUN paramètre de commission paritaire. Le net se
// calcule sur le brut, le statut, la situation familiale et le régime — pas
// sur la CP. Une page qui laisserait entendre que « le net dépend de votre CP »
// mentirait. Ce que la CP décide, c'est le MINIMUM et le STATUT, pas la formule.
//
// ⚠️ LE STATUT N'EST PAS DÉDUIT DE LA GRILLE. `uniteBareme: 'horaire'` est un
// indice fort (les secteurs payés à l'heure sont ouvriers), mais ce n'est pas
// la vérité juridique : elle vient de l'arrêté royal instituant la commission.
// Déduire « horaire ⇒ ouvrier » serait inventer un mécanisme (R2 bis). Le
// statut est donc un CHOIX offert au visiteur, jamais une affirmation de notre
// part, et les deux résultats sont calculés pour qu'il puisse comparer.
// ═══════════════════════════════════════════════════════════════
import { calcPayroll, calcCotisationsSectorielles } from '@/app/lib/payroll';
import { optsPalier } from '@/app/lib/periode-palier';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// ⚠️ SANS `{ date }`, `calcPayroll` applique le palier werkbonus BASELINE
// (le plus ANCIEN, 01/04/2026 — voir le moteur, fonction `werkbonusPalier`),
// pas celui du mois. `periode-palier.js` existe exactement pour ce piège
// (« un écran, une API, un export, un document → optsPalier(periode) ») et
// cette page publique ne l'utilisait pas : mesuré le 17/09/2026, ce
// calculateur sous-estimait le net de 16 à 23 €/mois sur les revenus encore
// dans la zone du bonus à l'emploi (palier d'avril contre celui de septembre,
// `lois-belges.js` → `pp.werkbonusSocialParPalier`). `coutEmployeur` n'en
// dépend pas (pas de werkbonus côté employeur) : lui reste inchangé.
//
// ⚠️ Deux sessions ont corrigé ce même bug le même jour (17/09/2026) : celle-ci
// via le helper canonique `optsPalier()` (R14 bis — une règle qui gouverne un
// montant s'écrit une fois et s'importe), l'autre via un `_dateDuJour()` local
// dupliqué. La version retenue au merge est `optsPalier()` : c'est déjà la
// source unique utilisée par `periode-palier.js` et par `avis-paiements-core`,
// un second helper local aurait recréé exactement la duplication que R14 bis
// interdit.

/** Statuts proposés au visiteur. Aucun n'est présenté comme « le vôtre ». */
export const STATUTS = ['employe', 'ouvrier'];

/**
 * Une ligne de calcul complète pour un brut et un statut donnés.
 *
 * Hypothèses AFFICHÉES sur la page, pas cachées ici : isolé, sans enfant à
 * charge, temps plein. Elles sont passées EXPLICITEMENT et POSITIONNELLEMENT
 * au moteur — `calcPayroll(brut, statut, familial, charges, regime)`.
 *
 * ⚠️ Ne jamais appeler `calcPayroll(brut, { statut: … })` : la signature est
 * positionnelle, l'objet atterrit dans `statut` et le reste passe à
 * `undefined`. Le résultat retombe sur ses pieds par accident (isolé, 0 enfant,
 * temps plein) — mais le code ment sur ce qu'il fait, et le `cp` qu'on croit
 * passer est purement ignoré.
 */
export function calculer(brut, { statut = 'employe', cp = null } = {}) {
  const b = r2(brut);
  if (!(b > 0)) return null;
  const R = calcPayroll(b, statut, 'isole', 0, 100, optsPalier());
  const sect = cp ? calcCotisationsSectorielles(b, cp, statut) : null;

  return {
    brut: r2(R.brut),
    statut,
    onssTravailleur: r2(R.onssP),
    bonusEmploi: r2(R.bonusEmploi),
    imposable: r2(R.imposable),
    precompte: r2(R.pp),
    csss: r2(R.csss),
    net: r2(R.net),
    // Coût employeur COMPLET (ONSS patronal + AT + fonds sectoriels + médecine
    // du travail). `brut × taux facial` seul le sous-estime — c'est la leçon
    // déjà tirée sur la page « coût employeur ».
    coutEmployeur: r2(R.coutEmployeurComplet),
    // Part sectorielle, celle qui change d'une CP à l'autre.
    fondsSectoriel: sect ? r2(sect.fondsSE) : null,
    fondsSectorielNom: sect ? sect.fondsSE_nom : null,
    // Le second pilier sectoriel (codes ONSS 825/835) : une cotisation
    // patronale réelle, perçue sur 35 couples CP × public, qui n'apparaissait
    // nulle part. Ligne SÉPARÉE — ce n'est pas un fonds de sécurité d'existence.
    pensionSectorielle: sect ? r2(sect.pensionSectorielle) : null,
    isOuvrier: !!R.isOuvrier,
  };
}

/**
 * Les trois points qui rendent la page utile SANS interaction — donc lisibles
 * par un robot qui n'exécute pas de JavaScript, et par quelqu'un qui arrive de
 * Google et repart en dix secondes.
 *
 * Le minimum sectoriel, puis deux marches au-dessus. Les pourcentages ne sont
 * pas des valeurs légales : ce sont des repères d'affichage, d'où leur présence
 * ici et pas dans lois-belges.js.
 */
export const MARCHES = [0, 0.10, 0.25];

export function grilleAutour(minimum, opts = {}) {
  const m = r2(minimum);
  if (!(m > 0)) return [];
  return MARCHES.map((p) => calculer(r2(m * (1 + p)), opts)).filter(Boolean);
}

/** Écart de net entre les deux statuts, à brut égal. Rend null si non calculable. */
export function ecartStatuts(brut, cp) {
  const e = calculer(brut, { statut: 'employe', cp });
  const o = calculer(brut, { statut: 'ouvrier', cp });
  if (!e || !o) return null;
  return { employe: e, ouvrier: o, ecartNet: r2(e.net - o.net), ecartCout: r2(o.coutEmployeur - e.coutEmployeur) };
}
